import finishedSubIcon from '@/assets/icons/finished-sub-icon.png';
import inProgressSubIcon from '@/assets/icons/in-progress-sub-icon.png';
import notStartedSubIcon from '@/assets/icons/not-started-sub-icon.png';
import { Box, Typography } from '@mui/material';
import { getSubprojectStatusIcon } from '../../statusUtils';
import { Subproject } from './types';
import { getOverallStatus } from './utils';

interface SubprojectsDisplayProps {
  subprojects: Subproject[];
  isPencilsDown: boolean;
  timeAgo: string;
}

export function SubprojectsDisplay({
  subprojects,
  isPencilsDown,
  timeAgo,
}: SubprojectsDisplayProps) {
  // Display summary counts for >10 subprojects
  if (subprojects.length > 10) {
    const finishedCount = subprojects.filter(
      (s) => getOverallStatus(s) === 'finished',
    ).length;
    const inProgressCount = subprojects.filter(
      (s) => getOverallStatus(s) === 'in-progress',
    ).length;
    const notStartedCount = subprojects.filter(
      (s) => getOverallStatus(s) === 'not-started',
    ).length;

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            gap: 'clamp(12px, 1.04vw, 20px)',
            alignItems: 'center',
          }}
        >
          {/* Finished */}
          <Box
            sx={{
              display: 'flex',
              gap: 'clamp(6px, 0.52vw, 10px)',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Futura PT, sans-serif',
                fontWeight: 500,
                fontSize: 'clamp(9px, 0.625vw, 12px)',
                color: isPencilsDown
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(19, 54, 86, 0.7)',
              }}
            >
              {finishedCount}
            </Typography>
            <Box
              component='img'
              src={finishedSubIcon}
              alt='finished'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(9px, 0.71vw, 14px)',
                display: 'block',
                imageRendering: 'crisp-edges',
              }}
            />
          </Box>

          {/* In Progress */}
          <Box
            sx={{
              display: 'flex',
              gap: 'clamp(6px, 0.52vw, 10px)',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Futura PT, sans-serif',
                fontWeight: 500,
                fontSize: 'clamp(9px, 0.625vw, 12px)',
                color: isPencilsDown
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(19, 54, 86, 0.7)',
              }}
            >
              {inProgressCount}
            </Typography>
            <Box
              component='img'
              src={inProgressSubIcon}
              alt='in-progress'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(9px, 0.71vw, 14px)',
                display: 'block',
                imageRendering: 'crisp-edges',
              }}
            />
          </Box>

          {/* Not Started */}
          <Box
            sx={{
              display: 'flex',
              gap: 'clamp(6px, 0.52vw, 10px)',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Futura PT, sans-serif',
                fontWeight: 500,
                fontSize: 'clamp(9px, 0.625vw, 12px)',
                color: isPencilsDown
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(19, 54, 86, 0.7)',
              }}
            >
              {notStartedCount}
            </Typography>
            <Box
              component='img'
              src={notStartedSubIcon}
              alt='not-started'
              sx={{
                width: 'clamp(28px, 2.2vw, 42px)',
                height: 'clamp(9px, 0.71vw, 14px)',
                display: 'block',
                imageRendering: 'crisp-edges',
              }}
            />
          </Box>
        </Box>

        {/* Timestamp below */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 'clamp(9px, 0.625vw, 12px)',
            color: isPencilsDown
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(19, 54, 86, 0.4)',
          }}
        >
          Updated {timeAgo}
        </Typography>
      </>
    );
  }

  // Display individual icons for <=10 subprojects
  return (
    <>
      {/* First row (up to 6) */}
      <Box
        sx={{
          display: 'flex',
          gap: 'clamp(6px, 0.52vw, 10px)',
          justifyContent: 'flex-start',
        }}
      >
        {subprojects.slice(0, 6).length > 0 ? (
          subprojects.slice(0, 6).map((sub) => (
            <Box
              key={sub.id}
              component='img'
              src={getSubprojectStatusIcon(getOverallStatus(sub))}
              alt={getOverallStatus(sub)}
              sx={{
                width: {
                  xs: 'clamp(24px, 1.8vw, 36px)',
                  lg: 'clamp(28px, 2.2vw, 42px)',
                  '@media (min-width: 1700px)': {
                    width: 'clamp(24px, 1.8vw, 36px)',
                  },
                },
                height: {
                  xs: 'clamp(8px, 0.6vw, 12px)',
                  lg: 'clamp(9px, 0.71vw, 14px)',
                  '@media (min-width: 1700px)': {
                    height: 'clamp(8px, 0.6vw, 12px)',
                  },
                },
                display: 'block',
                imageRendering: 'crisp-edges',
                flexShrink: 0,
              }}
            />
          ))
        ) : (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(9px, 0.625vw, 12px)',
              color: isPencilsDown
                ? 'rgba(255, 255, 255, 0.4)'
                : 'rgba(19, 54, 86, 0.4)',
            }}
          >
            Updated {timeAgo}
          </Typography>
        )}
      </Box>

      {/* Second row (7-10) */}
      {(subprojects.slice(6, 10).length > 0 ||
        (subprojects.slice(0, 6).length > 0 &&
          subprojects.slice(0, 6).length <= 6)) && (
        <Box
          sx={{
            display: 'flex',
            gap: 'clamp(6px, 0.52vw, 10px)',
            justifyContent: 'flex-start',
          }}
        >
          {subprojects.slice(6, 10).length > 0 ? (
            subprojects.slice(6, 10).map((sub) => (
              <Box
                key={sub.id}
                component='img'
                src={getSubprojectStatusIcon(getOverallStatus(sub))}
                alt={getOverallStatus(sub)}
                sx={{
                  width: {
                    xs: 'clamp(24px, 1.8vw, 36px)',
                    lg: 'clamp(28px, 2.2vw, 42px)',
                    '@media (min-width: 1700px)': {
                      width: 'clamp(24px, 1.8vw, 36px)',
                    },
                  },
                  height: {
                    xs: 'clamp(8px, 0.6vw, 12px)',
                    lg: 'clamp(9px, 0.71vw, 14px)',
                    '@media (min-width: 1700px)': {
                      height: 'clamp(8px, 0.6vw, 12px)',
                    },
                  },
                  display: 'block',
                  imageRendering: 'crisp-edges',
                  flexShrink: 0,
                }}
              />
            ))
          ) : (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 'clamp(9px, 0.625vw, 12px)',
                color: isPencilsDown
                  ? 'rgba(255, 255, 255, 0.4)'
                  : 'rgba(19, 54, 86, 0.4)',
              }}
            >
              Updated {timeAgo}
            </Typography>
          )}
        </Box>
      )}

      {/* Timestamp below both rows when both rows have subprojects */}
      {subprojects.slice(6, 10).length > 0 && (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 'clamp(9px, 0.625vw, 12px)',
            color: isPencilsDown
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(19, 54, 86, 0.4)',
          }}
        >
          Updated {timeAgo}
        </Typography>
      )}
    </>
  );
}
